import React, { useState, useEffect } from 'react';
import PhoneFilters from '../components/PhoneFilters';
import { TrendingUp, TrendingDown, CheckCircle, AlertCircle, Clock, Download, Search, Filter } from 'lucide-react';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';

const ContributionBalancePage = () => {
  const [balances, setBalances] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [nextDueDate, setNextDueDate] = useState('');

  useEffect(() => {
    loadBalances();
  }, []);

  const loadBalances = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contribution-balance');
      setBalances(response.data.data || []);
      setSummary(response.data.summary || {});
      setNextDueDate(response.data.next_due_date || '');
    } catch (error) {
      console.error('Failed to load contribution balances:', error);
      toast.error('Failed to load contribution balances');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'up_to_date':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'on_track':
        return <TrendingUp size={16} className="text-blue-600" />;
      case 'behind':
        return <Clock size={16} className="text-yellow-600" />;
      case 'critical':
        return <AlertCircle size={16} className="text-red-600" />;
      default:
        return <Clock size={16} className="text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      up_to_date: { bg: 'bg-green-100', text: 'text-green-800', label: 'Up to Date' },
      on_track: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'On Track' },
      behind: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Behind' },
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
    };
    const config = configs[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {getStatusIcon(status)}
        <span className="ml-1">{config.label}</span>
      </span>
    );
  };

  const filteredBalances = balances.filter(balance => {
    const matchesSearch = balance.member_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         balance.member_no?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || balance.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const exportToCSV = () => {
    const headers = ['Member No', 'Member Name', 'Expected', 'Contributed', 'Balance', 'Percentage', 'Status'];
    const rows = filteredBalances.map(b => [
      b.member_no || '',
      b.member_name,
      b.expected,
      b.contributed,
      b.balance,
      b.percentage,
      b.status
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contribution-balances-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported to CSV');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contribution balances...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">Payment Routing Not Configured</h3>
          <p className="text-sm text-yellow-700">
            Payment routing settings have not been configured for your group. 
            Please contact your administrator to set up contribution targets.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Contribution Balances</h1>
        <p className="mt-2 text-gray-600">
          Track member contribution progress and balances
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 kpi-grid">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Expected</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                KES {summary.total_expected?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <TrendingUp className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Contributed</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                KES {summary.total_contributed?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Balance</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                KES {summary.total_balance?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <TrendingDown className="text-red-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Average Progress</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {summary.average_percentage?.toFixed(1) || 0}%
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <Clock className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Status Summary */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Member Status Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 kpi-grid">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-3xl font-bold text-green-600">{summary.members_up_to_date || 0}</p>
            <p className="text-sm text-gray-600 mt-1">Up to Date</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-3xl font-bold text-blue-600">{summary.members_on_track || 0}</p>
            <p className="text-sm text-gray-600 mt-1">On Track</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <p className="text-3xl font-bold text-yellow-600">{summary.members_behind || 0}</p>
            <p className="text-sm text-gray-600 mt-1">Behind</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-3xl font-bold text-red-600">{summary.members_critical || 0}</p>
            <p className="text-sm text-gray-600 mt-1">Critical</p>
          </div>
        </div>
        {nextDueDate && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Next Due Date:</strong> {new Date(nextDueDate).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </p>
          </div>
        )}
      </div>

      {/* Filters and Search */}
      <PhoneFilters count={(statusFilter !== 'all' ? 1 : 0) + (searchTerm ? 1 : 0)}>
<div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name or member number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-gray-600" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="up_to_date">Up to Date</option>
                <option value="on_track">On Track</option>
                <option value="behind">Behind</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download size={20} />
              Export CSV
            </button>
          </div>
        </div>
      </div>
      </PhoneFilters>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="responsive-table min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contributed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBalances.map((balance) => (
                <tr key={balance.member_id} className="hover:bg-gray-50">
                  <td data-label="Member" className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{balance.member_name}</div>
                      {balance.member_no && (
                        <div className="text-sm text-gray-500">{balance.member_no}</div>
                      )}
                    </div>
                  </td>
                  <td data-label="Expected" className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    KES {balance.expected.toLocaleString()}
                  </td>
                  <td data-label="Contributed" className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                    KES {balance.contributed.toLocaleString()}
                  </td>
                  <td data-label="Balance" className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <span className={balance.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                      KES {balance.balance.toLocaleString()}
                    </span>
                  </td>
                  <td data-label="Progress" className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className={`h-2 rounded-full ${
                            balance.status === 'up_to_date' ? 'bg-green-500' :
                            balance.status === 'on_track' ? 'bg-blue-500' :
                            balance.status === 'behind' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(balance.percentage, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-900 font-medium">
                        {balance.percentage.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td data-label="Status" className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(balance.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredBalances.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No members found matching your filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContributionBalancePage;
